import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { handleDeleteWebsite, handleUserWebsites } from "../axios";
import { useSelector } from "react-redux";
import { ArrowLeftIcon, DownloadCloudIcon, TrashIcon } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomLoader from "./CustomLoader";
import NoDataComponent from "./NoDataComponent";
import { convertToIST } from "../utils/utils";
import Swal from "sweetalert2";

const WebsitesUserSelected = () => {
  const { token } = useSelector((state) => state.auth);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();

  const email = searchParams.get("email");

  const DeleteWebsite = async (email, website) => {
    const config = {
      headers: {
        Authorization: "Bearer " + token,
      },
    };
    try {
      const res = await handleDeleteWebsite(email, website, config);

      if (res?.status === 200) {
        Swal.fire({
          icon: "success",
          text: res.data.message,
        });
        handleGetUsersWebsites();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const columns = [
    {
      name: "S.No",
      selector: (row, index) => index + 1,
      sortable: true,
      width: "100px",
    },
    {
      name: "Website",
      selector: (row) => row.siteurl,
      sortable: true,
      width: "350px",
    },
    {
      name: "Created User Name",
      selector: (row) => row.userName,
      sortable: true,
      width: "250px",
    },
    {
      name: "Created User Email",
      selector: (row) => row.userEmail,
      sortable: true,
      width: "250px",
    },
    {
      name: "Selected",
      selector: (row) => row.selectedSite,
      sortable: true,
      cell: (row) => <span>{row.selectedSite ? "True" : "False"}</span>,
      width: "100px",
    },
    {
      name: "Created At",
      selector: (row) => convertToIST(row.createdAt),
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <div
          className="p-2 hover:bg-gray-200 cursor-pointer rounded-full"
          onClick={() => DeleteWebsite(row.userEmail, row.siteurl)}
        >
          <TrashIcon className="text-red-600" size={18} />
        </div>
      ),
      sortable: true,
    },
  ];

  const handleGetUsersWebsites = async () => {
    const config = {
      headers: {
        Authorization: "Bearer " + token,
      },
    };
    setLoading(true);
    try {
      const res = await handleUserWebsites(config);

      if (res?.status === 200) {
        // Update to use the 'websites' array from the response
        setUsers(res?.data?.websites || []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetUsersWebsites();
  }, []);

  // Filter users based on search term and email
  const filteredUsers = users.filter(
    (user) =>
      user.selectedSite &&
      (!email || user.userEmail === email) && // Skip email filtering if no email is provided
      (user.userId.includes(searchTerm) ||
        user.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.siteurl.includes(searchTerm))
  );

  const customStyles = {
    headCells: {
      style: {
        borderRight: "1px solid #ddd",
        fontWeight: "bold",
        letterSpacing: "0em",
        alignItems: "center",
      },
    },
    cells: {
      style: {
        borderRight: "1px solid #ddd",
        alignItems: "center",
      },
    },
  };

  const handleDownload = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," +
      "Website,Created User Name,Created User Email,Selected,Created At\n" +
      filteredUsers
        .map(
          (row) =>
            `"${row.siteurl}",${row.userName},${row.userEmail},${
              row.selectedSite
            },"${convertToIST(row.createdAt)}"`
        )
        .join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "WebsitesUserSelected_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="font-semibold antialiased tracking-normal text-[22px]">
          Websites User's Selected
        </p>
        <button
          onClick={() => navigate(-1)}
          className="bg-black text-white h-10 2xl:w-[130px] xl:w-[130px] lg:w-[130px] md:w-[130px] w-full rounded-[8px] flex justify-center items-center gap-2 hover:ring-2 hover:ring-gray-300 transition-all duration-300"
        >
          <ArrowLeftIcon />
          <p>Back</p>
        </button>
      </div>
      <div className="my-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="outline-none ring-1 ring-gray-200 2xl:w-[300px] xl:w-[300px] lg:w-[300px] md:w-[300px] w-full h-10 rounded-[8px] indent-3 mt-2 focus:ring-2 focus:ring-indigo-300 transition-all duration-300"
          />
          <button
            onClick={handleDownload}
            className="bg-[#BC3810] text-white h-10 2xl:w-[230px] xl:w-[230px] lg:w-[230px] md:w-[230px] w-full rounded-[8px] flex justify-center items-center gap-2 hover:ring-2 hover:ring-[#f1a791] transition-all duration-300"
          >
            <DownloadCloudIcon />
            <p>Download Report </p>
          </button>
        </div>
      </div>
      <div className="border border-gray-200 mt-2">
        <DataTable
          columns={columns}
          data={filteredUsers}
          pagination
          paginationRowsPerPageOptions={[10, 50, 100, 500]}
          highlightOnHover
          progressPending={loading}
          progressComponent={loading ? <CustomLoader /> : "NoData"}
          customStyles={customStyles}
          noDataComponent={<NoDataComponent />}
          responsive
        />
      </div>
    </div>
  );
};

export default WebsitesUserSelected;
